import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Row, Select, Space, Table, Tag, message } from 'antd';
import { useLazyQuery, useQuery, gql } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTaskStatusMeta } from './utils/workflowStatus';
import { resolveWorkflowErrorMessage } from './utils/workflowErrorMessage';
import { formatInstantInIanaZone } from '../../../utils/timezoneFormatter';
import { WORKFLOW_BIZ_TYPE_DEPOSIT_INSURANCE_UPLOAD, WORKFLOW_BIZ_TYPE_QC_PENALTY_UPLOAD } from './constants/workflowBizTypes';

const { RangePicker } = DatePicker;

interface WorkflowReviewTaskRow {
  instanceId: number;
  templateName: string;
  bizType: string;
  bizId: number;
  taskStatus: number;
  reviewResult: number | null;
  currentNodeName?: string | null;
  initiatedAt: string;
}

const WORKFLOW_REVIEW_TEMPLATE_OPTIONS = gql`
  query WorkflowReviewTemplateOptions {
    workflowReviewTemplateOptions {
      name
      bizType
    }
  }
`;

const WORKFLOW_REVIEW_TASKS = gql`
  query WorkflowReviewTasks(
    $bizType: String
    $templateName: String
    $taskStatus: Int
    $reviewResult: Int
    $initiatedFrom: String
    $initiatedTo: String
    $page: Int!
    $size: Int!
  ) {
    workflowReviewTasks(
      bizType: $bizType
      templateName: $templateName
      taskStatus: $taskStatus
      reviewResult: $reviewResult
      initiatedFrom: $initiatedFrom
      initiatedTo: $initiatedTo
      page: $page
      size: $size
    ) {
      content {
        instanceId
        templateName
        bizType
        bizId
        taskStatus
        reviewResult
        currentNodeName
        initiatedAt
      }
      page
      size
      totalElements
      totalPages
      hasNext
      hasPrevious
    }
  }
`;

const WorkflowAdjustmentReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const routePrefix = location.pathname.startsWith('/qualitycontrol') ? '/qualitycontrol/settlement' : '/settlement';
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [searched, setSearched] = useState(false);
  const hasAutoQueriedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();
  const { data: templateOptionsData, loading: templateOptionsLoading } = useQuery(
    WORKFLOW_REVIEW_TEMPLATE_OPTIONS,
    { fetchPolicy: 'cache-first' },
  );
  const workflowTemplateOptions = (templateOptionsData?.workflowReviewTemplateOptions ?? []).map(
    (item: { name: string; bizType: string }) => ({
      label: item.name,
      value: item.bizType,
    }),
  );
  const [queryTasks, { data, loading }] = useLazyQuery(WORKFLOW_REVIEW_TASKS, {
    fetchPolicy: 'network-only',
  });

  const getErrorMessage = (error: unknown) =>
    resolveWorkflowErrorMessage(error, '查询审批列表失败，请稍后重试');

  const runQuery = useCallback(
    async (opts: { forceShowResults?: boolean; silent?: boolean; page: number; pageSize: number }) => {
      const { forceShowResults = true, silent = false, page: nextPage, pageSize: nextPageSize } = opts;
      try {
        const values = await form.validateFields();
        const range = values.initiatedRange;
        await queryTasks({
          variables: {
            bizType: values.bizType || undefined,
            templateName: values.templateName || undefined,
            taskStatus: values.taskStatus,
            reviewResult: values.reviewResult,
            initiatedFrom: range?.[0]?.format('YYYY-MM-DD'),
            initiatedTo: range?.[1]?.format('YYYY-MM-DD'),
            page: nextPage,
            size: nextPageSize,
          },
        });
        setPage(nextPage);
        setPageSize(nextPageSize);
        setSearched(forceShowResults);
      } catch (error) {
        if (!silent) {
          message.error(getErrorMessage(error));
        }
      }
    },
    [form, queryTasks],
  );

  useEffect(() => {
    if (hasAutoQueriedRef.current) {
      return;
    }
    hasAutoQueriedRef.current = true;
    void runQuery({ silent: true, forceShowResults: true, page: 1, pageSize: 20 });
  }, [runQuery]);

  const taskStatusOptions = [
    { label: getTaskStatusMeta(1, t).label, value: 1 },
    { label: getTaskStatusMeta(2, t).label, value: 2 },
    { label: getTaskStatusMeta(3, t).label, value: 3 },
    { label: getTaskStatusMeta(4, t).label, value: 4 },
    { label: getTaskStatusMeta(5, t).label, value: 5 },
  ];

  const reviewResultOptions = [
    { label: getTaskStatusMeta(3, t).label, value: 3 },
    { label: getTaskStatusMeta(4, t).label, value: 4 },
  ];

  return (
    <Card title="流程审核页面">
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ bizType: (location.state as { bizType?: string } | null)?.bizType }}
      >
        <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50/80 px-5 py-5">
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <Form.Item label="任务流程" name="bizType" className="mb-0">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="请选择流程类型"
                  loading={templateOptionsLoading}
                  options={workflowTemplateOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <Form.Item label="任务状态" name="taskStatus" className="mb-0">
                <Select allowClear placeholder="请选择" options={taskStatusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <Form.Item label="审核结果" name="reviewResult" className="mb-0">
                <Select allowClear placeholder="请选择" options={reviewResultOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12} xl={8}>
              <Form.Item label="任务发起时间" name="initiatedRange" className="mb-0">
                <RangePicker className="w-full min-w-0" />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <Row className="mb-6">
          <Col xs={24} className="text-right">
            <Space size="middle">
              <Button
                type="primary"
                onClick={() => void runQuery({ silent: false, page: 1, pageSize })}
                loading={loading}
              >
                查询
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  void runQuery({ silent: false, forceShowResults: true, page: 1, pageSize: 20 });
                }}
              >
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      <Table
        style={{ marginTop: 0 }}
        rowKey="instanceId"
        loading={loading}
        dataSource={searched ? (data?.workflowReviewTasks?.content ?? []) : []}
        locale={{ emptyText: searched ? '暂无数据' : '请先输入条件并点击查询' }}
        pagination={
          searched
            ? {
                current: page,
                pageSize,
                total: Number(data?.workflowReviewTasks?.totalElements ?? 0),
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `共 ${total} 条`,
                onChange: (nextPage, nextSize) =>
                  void runQuery({
                    silent: false,
                    page: nextPage,
                    pageSize: nextSize ?? pageSize,
                  }),
              }
            : false
        }
        columns={[
          { title: '任务流程', dataIndex: 'templateName' },
          {
            title: '业务ID',
            dataIndex: 'bizId',
            render: (bizId: number, row: WorkflowReviewTaskRow) =>
              row.bizType === WORKFLOW_BIZ_TYPE_QC_PENALTY_UPLOAD || row.bizType === WORKFLOW_BIZ_TYPE_DEPOSIT_INSURANCE_UPLOAD
                ? `任务 ${bizId}`
                : bizId,
          },
          {
            title: '任务状态',
            dataIndex: 'taskStatus',
            render: (v: number) => {
              const meta = getTaskStatusMeta(v, t);
              return <Tag color={meta.color}>{meta.label}</Tag>;
            },
          },
          {
            title: '审核结果',
            dataIndex: 'reviewResult',
            render: (v: number | null) => {
              if (!v) return '-';
              const meta = getTaskStatusMeta(v, t);
              return <Tag color={meta.color}>{meta.label}</Tag>;
            },
          },
          { title: '当前节点', dataIndex: 'currentNodeName' },
          {
            title: '发起时间',
            dataIndex: 'initiatedAt',
            render: (time: string) => {
              if (!time) return '-';
              const date = new Date(time);
              return Number.isNaN(date.getTime()) ? '-' : formatInstantInIanaZone(date, browserTimeZone);
            },
          },
          {
            title: '操作',
            render: (_, row: WorkflowReviewTaskRow) => (
              <Button type="link" onClick={() => navigate(`${routePrefix}/adjustment-review/${row.instanceId}/${row.bizId}`)}>
                查看
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default WorkflowAdjustmentReviewPage;
